var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import set from "lodash.set";
import get from "lodash.get";
import { parseError } from "./errors";
import { warnOrErrorAsPrettyError } from "./logging";
const cleanDoi = (doi) => doi.replace(/https?:\/\/(dx.)?doi.org\//, '');
const setContentIfNotFalsy = ({ rcPath }) => (tagContent, rcItem) => {
    if (!tagContent)
        return rcItem;
    set(rcItem, rcPath, tagContent);
    return rcItem;
};
const setContentIfNotNullOrUndefined = ({ rcPath }) => (tagContent, rcItem) => {
    if (tagContent == null)
        return rcItem;
    set(rcItem, rcPath, tagContent);
    return rcItem;
};
const appendAuthor = (tagContent, rcItem) => {
    const authors = get(rcItem, 'article.authors', []);
    let author = tagContent;
    if (tagContent.includes(',')) {
        let nameParts = tagContent.split(',');
        const firstNamePart = (nameParts.pop() || "").trim();
        const lastNameParts = nameParts.join(',').trim();
        author = firstNamePart + ' ' + lastNameParts;
    }
    if (author) {
        authors.push(author);
        set(rcItem, 'article.authors', authors);
    }
    return rcItem;
};
const extractDoi = (tagContent, rcItem) => {
    const lowerContent = tagContent.toLowerCase();
    if (!lowerContent.includes('[doi]'))
        return rcItem;
    const trimmedContent = lowerContent
        .replace('[doi]', '')
        .trim();
    if (trimmedContent)
        set(rcItem, 'ext_ids.doi', cleanDoi(trimmedContent));
    return rcItem;
};
const extractDoiIfUnset = (tagContent, rcItem) => {
    var _a;
    if ((_a = rcItem === null || rcItem === void 0 ? void 0 : rcItem.ext_ids) === null || _a === void 0 ? void 0 : _a.doi)
        return rcItem;
    return extractDoi(tagContent, rcItem);
};
const setDefaultType = (tagContent, rcItem) => {
    set(rcItem, 'item_type', 'article');
    return rcItem;
};
const extractIssnOrEissn = (tagContent, rcItem) => {
    const lowerContent = tagContent.toLowerCase();
    const hasLinking = lowerContent.includes('(linking)');
    if (hasLinking) {
        const eissnContent = tagContent
            .replace(/\(linking\)/ig, '')
            .trim();
        set(rcItem, 'custom_metadata.eissn', eissnContent);
        return rcItem;
    }
    const hasPrint = lowerContent.includes('(print)');
    if (hasPrint) {
        const issnContent = tagContent
            .replace(/\(print\)/ig, '')
            .trim();
        set(rcItem, 'article.issn', issnContent);
        return rcItem;
    }
    return rcItem;
};
const extractYearDate = (tagContent, rcItem) => {
    var _a, _b, _c;
    const extractedYear = (_c = (_b = (_a = /(?<matchedYear>\d{1,4})/) === null || _a === void 0 ? void 0 : _a.exec(tagContent)) === null || _b === void 0 ? void 0 : _b.groups) === null || _c === void 0 ? void 0 : _c.matchedYear;
    if (extractedYear != null)
        set(rcItem, 'article.year', extractedYear);
    if (tagContent != null)
        set(rcItem, 'custom_metadata.date', tagContent);
    return rcItem;
};
const tagContentTransforms = {
    "PMID": setContentIfNotNullOrUndefined({ rcPath: 'ext_ids.pmid' }),
    "AB": setContentIfNotNullOrUndefined({ rcPath: 'article.abstract' }),
    "FAU": appendAuthor,
    "LID": extractDoi,
    "AID": extractDoiIfUnset,
    "PT": setDefaultType,
    "IS": extractIssnOrEissn,
    "DP": extractYearDate,
    "VI": setContentIfNotNullOrUndefined({ rcPath: 'article.volume' }),
    "PG": setContentIfNotNullOrUndefined({ rcPath: 'article.pagination' }),
    "TI": setContentIfNotFalsy({ rcPath: 'article.title' }),
    "LA": setContentIfNotFalsy({ rcPath: 'custom_metadata.language' }),
    "JT": setContentIfNotFalsy({ rcPath: 'article.journal' }),
    "TA": setContentIfNotFalsy({ rcPath: 'article.journal_abbrev' }),
};
export const nbibItemToRcItem = (nbibItem, text, errors, prettyErrors, hash) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e, _f;
    const entryText = text.slice(nbibItem.startLocation, nbibItem.endLocation);
    const originalId = yield hash(entryText);
    let rcItem = {
        import_data: {
            original_id: originalId
        }
    };
    const rcItemType = "article";
    set(rcItem, "item_type", rcItemType);
    const hasContent = (tag) => tag.content && tag.content.length;
    nbibItem
        .tags
        .filter(t => hasContent(t))
        .forEach(t => { var _a; return (_a = tagContentTransforms[t.tag]) === null || _a === void 0 ? void 0 : _a.call(tagContentTransforms, t.content, rcItem); });
    const hasTitleOrExtIds = ((_a = rcItem === null || rcItem === void 0 ? void 0 : rcItem.article) === null || _a === void 0 ? void 0 : _a.title) != null
        || (rcItem.ext_ids
            && Object.values(rcItem.ext_ids).filter(v => v).length);
    if (!hasTitleOrExtIds) {
        const error = parseError('Cannot convert. Ris item does not contain enough data. Ensure that item has a title or doi present.', nbibItem.startLocation);
        errors.push(error);
        const prettyError = warnOrErrorAsPrettyError(text, error);
        prettyErrors.push(prettyError);
        return;
    }
    const yearIsNotSetButDateIsAvailable = !((_b = rcItem === null || rcItem === void 0 ? void 0 : rcItem.article) === null || _b === void 0 ? void 0 : _b.year)
        && ((_c = rcItem === null || rcItem === void 0 ? void 0 : rcItem.custom_metadata) === null || _c === void 0 ? void 0 : _c.date);
    if (yearIsNotSetButDateIsAvailable) {
        if (!rcItem.article)
            rcItem.article = {};
        const date = rcItem.custom_metadata.date;
        const extractedYear = (_f = (_e = (_d = /(?<matchedYear>\d{1,4})/) === null || _d === void 0 ? void 0 : _d.exec(date)) === null || _e === void 0 ? void 0 : _e.groups) === null || _f === void 0 ? void 0 : _f.matchedYear;
        if (extractedYear)
            rcItem.article.year = extractedYear;
    }
    return rcItem;
});
